import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdSearch,
  MdViewModule,
  MdViewList,
  MdChevronLeft,
  MdChevronRight,
  MdPhone,
  MdSchool,
  MdCalendarToday,
  MdMailOutline,
} from 'react-icons/md';
import admissionsAPI from '../../api/admissionsAPI';
import { useEmployeeAttendanceAuth } from '../../context/EmployeeAttendanceAuthContext';
import { useEmployeeAttendanceHrAuth } from '../../context/EmployeeAttendanceHrAuthContext';
import { fetchWithHrToken, HR_SESSION_ERROR } from '../../utils/hrApiHelpers';
import {
  readSavedListFilters,
  writeSavedListFilters,
} from '../../utils/admissionsListFilters';
import '../../employeeAttendance.css';
import '../../employeeAttendance-hr.css';
import '../../employeeAttendance-admissions.css';

const PAGE_SIZE = 80;
const FILTER_KEY = 'prospective';

const CAMPUS_FALLBACK = [
  { value: 'prof_addow', label: 'Prof Addow Campus' },
  { value: 'prof_shahiid', label: 'Prof Shahiid Campus' },
  { value: 'prof_shurie', label: 'Prof Shurie Campus' },
];

const ADMITTED_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Admitted' },
  { value: 'false', label: 'Not admitted' },
];

function formatVisitDate(value) {
  if (!value) return '—';
  const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function readProspectiveFilter(key, fallback) {
  const saved = readSavedListFilters(FILTER_KEY);
  if (!saved?._ready) return fallback;
  return key in saved ? saved[key] : fallback;
}

export default function ProspectiveStudentsPage() {
  const navigate = useNavigate();
  const { ensureHrToken } = useEmployeeAttendanceHrAuth();
  const { user } = useEmployeeAttendanceAuth();

  const initialSavedRef = useRef((() => {
    const saved = readSavedListFilters(FILTER_KEY);
    return saved?._ready ? saved : null;
  })());

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(() => readProspectiveFilter('offset', 0));
  const [viewMode, setViewMode] = useState(() => readProspectiveFilter('viewMode', 'kanban'));
  const [searchInput, setSearchInput] = useState(() => readProspectiveFilter('searchInput', ''));
  const [search, setSearch] = useState(() => readProspectiveFilter('search', ''));
  const [campus, setCampus] = useState(() => readProspectiveFilter('campus', ''));
  const [facultyId, setFacultyId] = useState(() => readProspectiveFilter('facultyId', ''));
  const [isAdmitted, setIsAdmitted] = useState(() => readProspectiveFilter('isAdmitted', ''));
  const [academicYearId, setAcademicYearId] = useState(() =>
    readProspectiveFilter('academicYearId', '')
  );
  const [yearsReady, setYearsReady] = useState(false);
  const [campuses, setCampuses] = useState(CAMPUS_FALLBACK);
  const [faculties, setFaculties] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const searchInitRef = useRef(true);

  const openRecord = (id) => {
    navigate(`/bu-dashboard/admissions/prospective/${id}`);
  };

  const openNew = () => {
    navigate('/bu-dashboard/admissions/prospective/new');
  };

  const loadLookups = useCallback(async () => {
    const campusRes = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.getCampuses(token)
    );
    if (!campusRes.error) {
      const list = campusRes.data?.campuses || campusRes.data?.records || [];
      if (list.length) setCampuses(list);
    }
    const facRes = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.getFaculties(token)
    );
    if (!facRes.error) {
      setFaculties(facRes.data?.faculties || facRes.data?.records || []);
    }
    const yearRes = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.getAcademicYears(token)
    );
    if (!yearRes.error) {
      const years = yearRes.data?.academic_years || yearRes.data?.records || [];
      setAcademicYears(years);
      const preferred =
        years.find((y) => {
          const name = String(y.name || '').toLowerCase();
          return name.includes('2026-2027') && (name.includes('ug') || y.is_ug);
        }) ||
        years.find((y) => y.id === yearRes.data?.default_id) ||
        years.find((y) => y.is_current && y.is_ug) ||
        years.find((y) => y.is_current);
      if (preferred?.id && initialSavedRef.current === null) {
        setAcademicYearId(String(preferred.id));
      }
    }
    setYearsReady(true);
  }, [ensureHrToken]);

  const loadRecords = useCallback(async () => {
    if (!yearsReady) return;
    setLoading(true);
    setError(null);
    const params = {
      search: search || undefined,
      campus: campus || undefined,
      faculty_id: facultyId ? Number(facultyId) : undefined,
      academic_year_id: academicYearId ? Number(academicYearId) : undefined,
      limit: PAGE_SIZE,
      offset,
    };
    if (isAdmitted === 'true') params.is_admitted = true;
    if (isAdmitted === 'false') params.is_admitted = false;

    const { data, error: fetchError } = await fetchWithHrToken(ensureHrToken, (token) =>
      admissionsAPI.getProspectiveStudents(params, token)
    );
    if (fetchError === 'no_token') {
      setError(user?.hr_api_error || HR_SESSION_ERROR);
      setRecords([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    if (fetchError) {
      setError(fetchError);
      setRecords([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setRecords(data.records || []);
    setTotal(Number(data.total) || 0);
    setLoading(false);
  }, [ensureHrToken, search, campus, facultyId, isAdmitted, academicYearId, offset, yearsReady, user?.hr_api_error]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    if (!yearsReady) return;
    writeSavedListFilters(FILTER_KEY, {
      offset,
      viewMode,
      searchInput,
      search,
      campus,
      facultyId,
      isAdmitted,
      academicYearId,
      _ready: true,
    });
  }, [offset, viewMode, searchInput, search, campus, facultyId, isAdmitted, academicYearId, yearsReady]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    if (searchInitRef.current) {
      searchInitRef.current = false;
      return undefined;
    }
    const t = setTimeout(() => {
      setOffset(0);
      setSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  return (
    <div className="ea-hr-dash ea-odoo-emp ea-adm ea-prospective">
      <div className="ea-odoo-control">
        <div className="ea-odoo-control__left">
          <button type="button" className="ea-odoo-new" onClick={openNew}>
            New
          </button>
          <div className="ea-odoo-control__title">Prospective Students</div>
        </div>

        <div className="ea-odoo-search">
          <MdSearch className="ea-odoo-search__icon" aria-hidden />
          <input
            type="search"
            placeholder="Search name, phone, faculty..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search prospective students"
          />
        </div>

        <div className="ea-odoo-control__right">
          <div className="ea-odoo-view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={viewMode === 'kanban' ? 'active' : ''}
              onClick={() => setViewMode('kanban')}
              title="Kanban"
            >
              <MdViewModule size={18} />
            </button>
            <button
              type="button"
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
              title="List"
            >
              <MdViewList size={18} />
            </button>
          </div>
          <div className="ea-odoo-pager" aria-label="Pagination">
            <span>{loading ? '…' : `${rangeStart}-${rangeEnd} / ${total}`}</span>
            <button
              type="button"
              className="ea-odoo-pager__btn"
              disabled={!canPrev || loading}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              aria-label="Previous page"
            >
              <MdChevronLeft size={20} />
            </button>
            <button
              type="button"
              className="ea-odoo-pager__btn"
              disabled={!canNext || loading}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              aria-label="Next page"
            >
              <MdChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="ea-odoo-filters">
        <label>
          <span className="ea-field-label">Campus</span>
          <select
            className="ea-odoo-filters__select"
            value={campus}
            onChange={(e) => {
              setOffset(0);
              setCampus(e.target.value);
            }}
          >
            <option value="">All campuses</option>
            {campuses.map((c) => (
              <option key={c.value || c.id} value={c.value || c.id}>
                {c.label || c.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="ea-field-label">Faculty</span>
          <select
            className="ea-odoo-filters__select"
            value={facultyId}
            onChange={(e) => {
              setOffset(0);
              setFacultyId(e.target.value);
            }}
          >
            <option value="">All faculties</option>
            {faculties.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="ea-field-label">Admitted</span>
          <select
            className="ea-odoo-filters__select"
            value={isAdmitted}
            onChange={(e) => {
              setOffset(0);
              setIsAdmitted(e.target.value);
            }}
          >
            {ADMITTED_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="ea-field-label">Academic Year</span>
          <select
            className="ea-odoo-filters__select"
            value={academicYearId}
            onChange={(e) => {
              setOffset(0);
              setAcademicYearId(e.target.value);
            }}
          >
            <option value="">All academic years</option>
            {academicYears.map((y) => (
              <option key={y.id} value={y.id}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <div className="ea-odoo-alert ea-odoo-alert--error" role="alert">
          {error}
        </div>
      )}

      {viewMode === 'kanban' ? (
        <div className="ea-prospective-kanban">
          {loading && records.length === 0 ? (
            <p className="ea-hr-table__empty">Loading prospective students…</p>
          ) : records.length === 0 ? (
            <p className="ea-hr-table__empty">No prospective students found.</p>
          ) : (
            records.map((row) => (
              <article key={row.id} className="ea-prospective-card">
                <button
                  type="button"
                  className="ea-prospective-card__main"
                  onClick={() => openRecord(row.id)}
                  aria-label={`Open ${row.name}`}
                >
                  <div className="ea-prospective-card__bar" />
                  <h3 className="ea-prospective-card__name">{row.name}</h3>
                  <ul className="ea-prospective-card__meta">
                    {row.phone ? (
                      <li>
                        <MdPhone aria-hidden />
                        <span>{row.phone}</span>
                      </li>
                    ) : null}
                    {row.faculty_name ? (
                      <li>
                        <MdSchool aria-hidden />
                        <span>{row.faculty_name}</span>
                      </li>
                    ) : null}
                    {row.visit_date ? (
                      <li>
                        <MdCalendarToday aria-hidden />
                        <span>{formatVisitDate(row.visit_date)}</span>
                      </li>
                    ) : null}
                    {row.email ? (
                      <li>
                        <MdMailOutline aria-hidden />
                        <span>{row.email}</span>
                      </li>
                    ) : null}
                  </ul>
                </button>
              </article>
            ))
          )}
        </div>
      ) : (
        <div className="ea-odoo-list-wrap">
          <table className="ea-odoo-list">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Faculty</th>
                <th>Department</th>
                <th>Campus</th>
                <th>Visit Date</th>
                <th>Admitted</th>
              </tr>
            </thead>
            <tbody>
              {loading && records.length === 0 ? (
                <tr>
                  <td colSpan={7} className="ea-hr-table__empty">
                    Loading…
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={7} className="ea-hr-table__empty">
                    No prospective students found.
                  </td>
                </tr>
              ) : (
                records.map((row) => (
                  <tr
                    key={row.id}
                    className="ea-odoo-att-list__row"
                    onClick={() => openRecord(row.id)}
                  >
                    <td className="ea-odoo-list__name">{row.name}</td>
                    <td>{row.phone || ''}</td>
                    <td>{row.faculty_name || ''}</td>
                    <td>{row.department_name || ''}</td>
                    <td>{row.campus_label || row.campus || ''}</td>
                    <td>{formatVisitDate(row.visit_date)}</td>
                    <td>{row.is_admitted ? 'Yes' : 'No'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
